import { DateTime } from "luxon";

// The date column of a transformed shifts CSV. Set by the shifts transform prompt in
// `transform_csv.py` ("Column headers (in exact order): first_name,last_name,Start Date,...").
export const SHIFTS_DATE_COLUMN = "Start Date";

// The calendar formats the LOADER accepts, mirroring `date_formats` in `shifts_loader.py:parse_date`.
// Deliberately no date-time or offset forms: a cell the loader would skip must not become a sort key
// here, or the order shown would be derived from a row that never lands in the schedule.
const DATE_FORMATS = ["M/d/yyyy", "yyyy-MM-dd", "M-d-yyyy", "yyyy/M/d", "d.M.yyyy", "LLL d, yyyy", "LLLL d, yyyy", "d LLL yyyy", "yyyyMMdd"] as const;

// Parsed in a fixed zone rather than the ambient one. These values are calendar dates with no time
// component, used only to compare files against each other, so pinning the zone keeps the comparison
// independent of the department Luxon happens to be defaulted to.
const PARSE_ZONE = "utc";

// Headers arrive padded and inconsistently cased in practice — the backend normalizes them the same
// way (`standardize_dataframe_columns`, `find_name_columns`), so matching exactly would silently
// find no date column and drop every file into "undated".
const normalizeHeader = (header: string): string => header.trim().toLowerCase();

/**
 * Split one CSV line on commas that are outside double quotes, unwrapping quoted cells.
 *
 * `parseCsvPreview` splits on every comma, which is correct for the preview grid it feeds (the
 * transform strips commas from the name columns precisely because of that). It is not safe here: a
 * quoted comma anywhere before the date column shifts the cell this function reads, and the file
 * would silently sort as undated rather than fail.
 */
function splitCsvLine(line: string): string[] {
	const cells: string[] = [];
	let cell = "";
	let inQuotes = false;
	for (let i = 0; i < line.length; i++) {
		const char = line[i];
		if (char === '"') {
			// A doubled quote inside a quoted cell is one literal quote (RFC 4180).
			if (inQuotes && line[i + 1] === '"') {
				cell += '"';
				i++;
			} else {
				inQuotes = !inQuotes;
			}
		} else if (char === "," && !inQuotes) {
			cells.push(cell);
			cell = "";
		} else {
			cell += char;
		}
	}
	cells.push(cell);
	return cells.map((c) => c.trim());
}

function parseCell(value: string): DateTime | null {
	const trimmed = value.trim();
	if (!trimmed) return null;
	for (const format of DATE_FORMATS) {
		const parsed = DateTime.fromFormat(trimmed, format, { zone: PARSE_ZONE });
		if (parsed.isValid) return parsed;
	}
	return null;
}

/**
 * Earliest date in one column of a transformed CSV, or null when the file has no usable date.
 *
 * The CSV is the right source rather than the file name or the Auto Prompt report: it is the exact
 * payload the load call sends, so the order shown to the onboarder cannot disagree with the order the
 * backend receives. An unparsable cell is skipped rather than defaulted — a wrong date here would
 * reorder the loads.
 */
export function earliestDateInCsvText(csv: string, column: string): DateTime | null {
	const lines = csv.split("\n").filter((line) => line.trim().length > 0);
	if (lines.length === 0) return null;

	const columnIndex = splitCsvLine(lines[0]).findIndex((header) => normalizeHeader(header) === normalizeHeader(column));
	if (columnIndex === -1) return null;

	let earliest: DateTime | null = null;
	for (const line of lines.slice(1)) {
		const parsed = parseCell(splitCsvLine(line)[columnIndex] ?? "");
		if (parsed && (!earliest || parsed < earliest)) earliest = parsed;
	}
	return earliest;
}

export function earliestDateInCsv(csv: ArrayBuffer, column: string): DateTime | null {
	return earliestDateInCsvText(new TextDecoder("utf-8").decode(csv), column);
}

/**
 * Earliest date in the blob a PreviewCard will actually upload.
 *
 * The preview's attempts filter only ever DROPS rows, but dropping a file's earliest rows raises its
 * earliest date, which can genuinely reorder it against another file — so the sort key has to come
 * from the filtered payload, not from the unfiltered buffer it was derived from. Async because reading
 * a Blob is; callers store the result rather than deriving it during render.
 */
export async function earliestDateInBlob(blob: Blob, column: string): Promise<DateTime | null> {
	return earliestDateInCsvText(await blob.text(), column);
}

// One row of the operator-facing "load order" list. `date` is what the file was sorted on — null means
// the file carries no date and is keeping its listed position.
export type LoadOrderEntry = {
	id: string;
	label: string;
	date: DateTime | null;
};

/**
 * Order files for loading: dated files oldest-first, then undated ones in the order they were listed.
 *
 * Shifts have to load chronologically — `insert_shifts` de-dupes only inside the file's own date range
 * and PTO/overlap conflicts are detected against what is already in the DB, so loading March before
 * February reports February's conflicts against a schedule that already holds the future.
 *
 * Roster and template files carry no date at all (they split a department by people, not by time), so
 * they pass `() => null` and keep their listed order. That order is still load-bearing: the create
 * path skips a row whose name/email/phone already exists, so for a person appearing in two roster
 * files the first file loaded wins.
 */
export function orderFilesChronologically<T>(items: T[], dateOf: (item: T) => DateTime | null): T[] {
	return items
		.map((item, index) => ({ item, index, date: dateOf(item) }))
		.sort((a, b) => {
			if (a.date && b.date) {
				const diff = a.date.toMillis() - b.date.toMillis();
				return diff !== 0 ? diff : a.index - b.index;
			}
			if (a.date) return -1;
			if (b.date) return 1;
			return a.index - b.index;
		})
		.map(({ item }) => item);
}
